import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

const STEP_NAMES = ["one", "two", "three", "four"] as const;

export function useDeliveryFormWizard(type: string, initialStep = 0) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const urlStep = searchParams.get("step");
  const [step, setStepState] = useState(urlStep ? Number.parseInt(urlStep, 10) : initialStep);

  const isTwoWay = type === "two-way-delivery";
  const totalSteps = isTwoWay ? 4 : 3;
  const isLastStep = step === totalSteps - 1;
  const steps = useMemo(() => Array.from({ length: totalSteps }, (_, i) => i), [totalSteps]);

  const updateUrlStep = (s: number) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("step", s.toString());
        return next;
      },
      { replace: true }
    );
  };

  const setStep = (s: number) => {
    setStepState(s);
    updateUrlStep(s);
  };

  const nextStep = () => {
    const next = step + 1;
    setStepState(next);
    updateUrlStep(next);
  };

  const prevStep = () => {
    const prev = step - 1;
    setStepState(prev);
    updateUrlStep(prev);
  };

  const getStepTitle = (s: number) => {
    const name = STEP_NAMES[s] ?? "four";
    return `${t("delivery.form.step")} ${s + 1} : ${t(`delivery.form.steps.step-${name}`)}`;
  };

  return {
    step,
    setStep,
    nextStep,
    prevStep,
    isTwoWay,
    totalSteps,
    isLastStep,
    steps,
    getStepTitle,
  } as const;
}
